import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { Plus, Pencil, Save, X } from 'lucide-react';
import useSettingLists from '../../wrappers/use-setting-list-item';
import { ISettingListItem, SettingListItem } from '../../../../shared/types/setting.types';
import { useTranslation } from 'react-i18next';
import {
    getSettingListEditingRowStyles,
    settingListsItemsStyles,
} from './SettingListsItems.styles';

interface ListConfig {
    name: string;
    label: string;
}

const SettingListsItems: React.FC = () => {
    const { t } = useTranslation();
    const { list: fetchList, create, edit } = useSettingLists();

    const lists: ListConfig[] = useMemo(() => [
        { name: 'dealers', label: t('settingLists.dealersTab') },
        { name: 'serviceTypes', label: t('settingLists.serviceTypesTab') },
    ], [t]);
    const [activeTab, setActiveTab] = useState(0);
    const [items, setItems] = useState<SettingListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ISettingListItem>>({});
    const [isNew, setIsNew] = useState(false);

    const activeList = lists[activeTab];

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setEditingId(null);
            setIsNew(false);
            const data = await fetchList(activeList.name);
            setItems(data);
            setLoading(false);
        };
        load();
    }, [activeList.name, fetchList]);

    const handleEdit = (item: SettingListItem) => {
        setEditingId(item.id);
        setEditForm({ label: item.label, description: item.description ?? '', blocked: item.blocked });
        setIsNew(false);
    };

    const handleAdd = () => {
        setEditingId('__new__');
        setEditForm({ label: '', description: '', blocked: false });
        setIsNew(true);
    };

    const handleSave = async () => {
        if (isNew) {
            const newItem = await create({
                list: activeList.name,
                label: editForm.label ?? '',
                blocked: editForm.blocked ?? false,
                description: editForm.description ?? '',
            });
            setItems((prev) => [...prev, newItem]);
        } else if (editingId) {
            await edit(editingId, {
                label: editForm.label ?? '',
                blocked: editForm.blocked ?? false,
                description: editForm.description,
            });
            setItems((prev) => prev.map((i) => i.id === editingId ? { ...i, ...editForm } as SettingListItem : i));
        }
        setEditingId(null);
        setIsNew(false);
    };

    const handleCancel = () => {
        setEditingId(null);
        setIsNew(false);
    };

    const handleTabChange = (_: React.SyntheticEvent, v: number) => {
        setActiveTab(v);
    };

    return (
        <Box sx={settingListsItemsStyles.root}>
            <Box>
                <Typography variant="h4" fontWeight={700}>{t('settingLists.title')}</Typography>
                <Typography color="text.secondary" mt={0.5}>{t('settingLists.subtitle')}</Typography>
            </Box>

            <Paper elevation={0} sx={settingListsItemsStyles.paper}>
                <Box sx={settingListsItemsStyles.tabsHeader}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        {lists.map((l) => (
                            <Tab key={l.name} label={l.label} sx={settingListsItemsStyles.tab} />
                        ))}
                    </Tabs>
                    <Button
                        startIcon={<Plus size={20} />}
                        size="small"
                        variant="contained"
                        onClick={handleAdd}
                        disabled={!!editingId || loading}
                        sx={settingListsItemsStyles.addButton}
                    >
                        {t('settingLists.addItem')}
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={settingListsItemsStyles.loadingContainer}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={settingListsItemsStyles.tableHeaderRow}>
                                    <TableCell sx={settingListsItemsStyles.tableHeaderCell}>{t('settingLists.colLabel')}</TableCell>
                                    <TableCell sx={settingListsItemsStyles.tableHeaderCell}>{t('settingLists.colDescription')}</TableCell>
                                    <TableCell sx={settingListsItemsStyles.tableHeaderCell}>{t('settingLists.colStatus')}</TableCell>
                                    <TableCell align="right" sx={settingListsItemsStyles.tableHeaderCell}>{t('settingLists.colActions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isNew && (
                                    <TableRow sx={settingListsItemsStyles.newRow}>
                                        <TableCell>
                                            <TextField
                                                size="small" fullWidth autoFocus
                                                value={editForm.label ?? ''}
                                                onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                                                placeholder={t('settingLists.labelPlaceholder')}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small" fullWidth
                                                value={editForm.description ?? ''}
                                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                                placeholder={t('settingLists.descriptionPlaceholder')}
                                            />
                                        </TableCell>
                                        <TableCell><Chip label={t('settingLists.statusActive')} color="success" size="small" /></TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" color="success" onClick={handleSave}><Save size={20} /></IconButton>
                                            <IconButton size="small" color="error" onClick={handleCancel}><X size={20} /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {items.map((item) => (
                                    <TableRow key={item.id} sx={getSettingListEditingRowStyles(editingId === item.id)} hover>
                                        <TableCell>
                                            {editingId === item.id ? (
                                                <TextField size="small" fullWidth autoFocus value={editForm.label ?? ''} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} />
                                            ) : (
                                                <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === item.id ? (
                                                <TextField size="small" fullWidth value={editForm.description ?? ''} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">{item.description ?? '—'}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingId === item.id ? (
                                                <Select
                                                    size="small"
                                                    value={editForm.blocked ? 'true' : 'false'}
                                                    onChange={(e) => setEditForm({ ...editForm, blocked: e.target.value === 'true' })}
                                                >
                                                    <MenuItem value="false">{t('settingLists.statusActive')}</MenuItem>
                                                    <MenuItem value="true">{t('settingLists.statusBlocked')}</MenuItem>
                                                </Select>
                                            ) : (
                                                <Chip label={item.blocked ? t('settingLists.statusBlocked') : t('settingLists.statusActive')} color={item.blocked ? 'default' : 'success'} size="small" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {editingId === item.id ? (
                                                <>
                                                    <IconButton size="small" color="success" onClick={handleSave}><Save size={16} /></IconButton>
                                                    <IconButton size="small" color="error" onClick={handleCancel}><X size={16} /></IconButton>
                                                </>
                                            ) : (
                                                <IconButton size="small" onClick={() => handleEdit(item)}><Pencil size={16} /></IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default SettingListsItems;
